import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { fetchUserDetails, getUserFromAccessToken, getUserFromUserAppsLocalStorage } from "../utils/auth";

const COOKIE_NAME = process.env.REACT_APP_AUTH_COOKIE_NAME || "access_token";
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL;
const APP_URL = process.env.REACT_APP_APP_URL;

function getTokenFromUrl(): string | null {
    const hash = window.location.hash?.slice(1) || "";
    const query = window.location.search?.slice(1) || "";
    const params = new URLSearchParams(hash || query);
    return params.get("access_token") ?? null;
}

function stripTokenFromUrl(): void {
    const withoutHash = window.location.pathname + window.location.search;
    const hashParams = new URLSearchParams(window.location.hash?.slice(1) || "");
    hashParams.delete("access_token");
    const newHash = hashParams.toString() ? `#${hashParams.toString()}` : "";
    const url = `${window.location.origin}${withoutHash}${newHash}`;
    window.history.replaceState(null, "", url);
}

type Props = {
    children: React.ReactNode;
};

const AuthGuard = ({ children }: Props) => {
    const [urlTokenHandled, setUrlTokenHandled] = useState(false);

    // On load: read token from URL, save it, fetch user details via API, then clean URL
    useEffect(() => {
        const tokenFromUrl = getTokenFromUrl();
        if (tokenFromUrl) {
            Cookies.set(COOKIE_NAME, tokenFromUrl, { sameSite: "lax" });
            try {
                localStorage.setItem(COOKIE_NAME, tokenFromUrl);
            } catch {
                // ignore
            }
            stripTokenFromUrl();
            (async () => {
                try {
                    let user = await fetchUserDetails(tokenFromUrl);
                    if (!user) {
                        user = getUserFromAccessToken(tokenFromUrl) ?? getUserFromUserAppsLocalStorage();
                    }
                    if (user) {
                        localStorage.setItem("user", JSON.stringify(user));
                    }
                } catch {
                    // ignore
                } finally {
                    setUrlTokenHandled(true);
                }
            })();
        } else {
            setUrlTokenHandled(true);
        }
    }, []);

    const accessToken =
        Cookies.get(COOKIE_NAME) ?? (typeof localStorage !== "undefined" ? localStorage.getItem(COOKIE_NAME) : null);

    if (process.env.REACT_APP_ENVIRONMENT === "dev" || process.env.REACT_APP_SKIP_AUTH === "true") {
        return <>{children}</>;
    }

    if (!urlTokenHandled) {
        return null; // wait one tick so we can read token from URL before redirecting
    }

    if (!accessToken) {
        const redirectUrl = APP_URL || window.location.origin;
        window.location.href = `${AUTH_SERVICE_URL}?redirect_url=${encodeURIComponent(redirectUrl)}`;
        return null;
    }

    return <>{children}</>;
};

export default AuthGuard;
